import { useState, useEffect, useRef, useCallback } from "react";
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { getAssociatedTokenAddress, createTransferInstruction, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { createAssociatedTokenAccountInstruction, ASSOCIATED_TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { getArenaPrice, calculateArenaAmount } from '../../birdeyeApi';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

// Audio file paths
const AUDIO_PATHS = {
  BALL_DROP: '/sounds/casino/button_click.mp3',
  PEG_HIT: '/sounds/casino/button_click.mp3',
  WIN_SMALL: '/win.wav',
  WIN_MEDIUM: '/win.wav',
  WIN_BIG: '/win.wav',
  LOSE: '/sounds/casino/error.mp3',
  AMBIENT: '/sounds/casino/ambient_casino.mp3',
  COINS: '/sounds/casino/coins_burst.mp3'
};

// ARENA Token Configuration
const ARENA_TOKEN_MINT = new PublicKey("HFqQ6xKTEyL2RSS7b8Ss5Sn4qgFqbL5b4rZmDhMhpkin");
const PROJECT_WALLET = new PublicKey("7SDrFeojsehbxuigUkJQVAoKDndh4LBuPVWsWwBd4JRm");
const ARENA_DECIMALS = 6;
const ALCHEMY_RPC_URL = 'https://solana-mainnet.g.alchemy.com/v2/pbGjxwq4DDspjqXb8DkO-Q4H-SzdtrLC';

// Plinko Configuration - PROPERLY SPACED
const ROWS = 12; // Reduced rows for better spacing
const BUCKET_COUNT = 9;
const BOARD_WIDTH = 500;
const BOARD_HEIGHT = 520; // Adjusted for better fit
const PEG_RADIUS = 4;
const PEG_SPACING = 36; // Slightly reduced for better bucket alignment
const START_Y = 60;
const ROW_HEIGHT = 35; // Adjusted for better fit
const BALL_RADIUS = 8;

const MULTIPLIERS = {
  low: [5.6, 2.1, 1.1, 1, 0.5, 1, 1.1, 2.1, 5.6],
  medium: [13, 3, 1.3, 0.7, 0.4, 0.7, 1.3, 3, 13],
  high: [29, 4, 1.5, 0.3, 0.2, 0.3, 1.5, 4, 29]
};

const BET_AMOUNTS_USD = [1, 2, 3, 4, 5, 10]; // USD amounts

export default function Plinko() {
  const { connection } = useConnection();
  const { publicKey, connected, disconnect, sendTransaction } = useWallet();
  const { setVisible } = useWalletModal();

  // Game state
  const [risk, setRisk] = useState('medium');
  const [betAmountUSD, setBetAmountUSD] = useState(1);
  const [isDropping, setIsDropping] = useState(false);
  const [currentBall, setCurrentBall] = useState(null);
  const [ballPath, setBallPath] = useState([]);
  const [dropHistory, setDropHistory] = useState([]);
  const [totalWagered, setTotalWagered] = useState(0);
  const [totalWon, setTotalWon] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoMode, setAutoMode] = useState(false);
  const [autoDropCount, setAutoDropCount] = useState(10);
  const [currentAutoDrops, setCurrentAutoDrops] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showBalanceHistory, setShowBalanceHistory] = useState(false);
  const [depositAmount, setDepositAmount] = useState(100);
  const [withdrawAmount, setWithdrawAmount] = useState(50);
  const [isDepositing, setIsDepositing] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("");
  const [withdrawStatus, setWithdrawStatus] = useState("");
  const [highlightedBucket, setHighlightedBucket] = useState(null);
  
  // Balance state
  const [userBalance, setUserBalance] = useState(0);
  const [userBalanceUSD, setUserBalanceUSD] = useState(0);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  
  // Wallet state
  const [arenaBalance, setArenaBalance] = useState(0);
  const [solBalance, setSolBalance] = useState(0);
  const [arenaPrice, setArenaPrice] = useState(0);
  const [isPriceLoading, setIsPriceLoading] = useState(true);
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  
  // Refs
  const boardRef = useRef(null);
  const audioRefs = useRef({});
  const ambientAudioRef = useRef(null);
  const animationRef = useRef(null);
  const autoDropIntervalRef = useRef(null);

  // Canvas setup for ball animation
  const canvasRef = useRef(null);
  const [canvasContext, setCanvasContext] = useState(null);

  // Icon components
  const SpeakerWaveIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
    </svg>
  );

  const SpeakerXMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );

  const WalletIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );

  const ArrowLeftIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );

  const ChartBarIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const PlayIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const StopIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  );

  const XMarkIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  const PlusIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const BanknotesIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  const ArrowUpIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
    </svg>
  );

  // Audio setup
  useEffect(() => {
    Object.entries(AUDIO_PATHS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      if (key === 'AMBIENT') {
        audio.loop = true;
        audio.volume = 0.3;
        ambientAudioRef.current = audio;
      } else {
        audio.volume = key.includes('WIN') ? 0.7 : 0.5;
        audioRefs.current[key] = audio;
      }
    });
    
    if (soundEnabled && ambientAudioRef.current) {
      ambientAudioRef.current.play().catch(e => console.log("Audio autoplay prevented:", e));
    }
    
    return () => {
      if (ambientAudioRef.current) {
        ambientAudioRef.current.pause();
        ambientAudioRef.current = null;
      }
      Object.values(audioRefs.current).forEach(audio => audio.pause());
      audioRefs.current = {};
    };
  }, []);

  // Sound toggle effect
  useEffect(() => {
    if (ambientAudioRef.current) {
      if (soundEnabled) {
        ambientAudioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      } else {
        ambientAudioRef.current.pause();
      }
    }
  }, [soundEnabled]);

  // Canvas setup
  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      setCanvasContext(ctx);
    }
  }, []);

  // Fetch ARENA price
  useEffect(() => {
    const fetchArenaPrice = async () => {
      setIsPriceLoading(true);
      try {
        const price = await getArenaPrice();
        setArenaPrice(price);
      } catch (error) {
        console.error('Error fetching ARENA price:', error);
        setArenaPrice(0.001);
      } finally {
        setIsPriceLoading(false);
      }
    };

    fetchArenaPrice();
    const interval = setInterval(fetchArenaPrice, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch user balance
  const fetchUserBalance = async () => {
    if (!connected || !publicKey) {
      setUserBalance(0);
      setUserBalanceUSD(0);
      setIsLoadingBalance(false);
      return;
    }

    try {
      console.log('Fetching balance for wallet:', publicKey.toString());
      
      const token = getValidToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization header if we have a valid token
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api/balance/get?walletAddress=${publicKey.toString()}&arenaPrice=${arenaPrice}`, {
        headers
      });

      const data = await response.json();
      console.log('Balance response:', data);
      
      if (data.success) {
        setUserBalance(data.balance.arena);
        setUserBalanceUSD(data.balance.arenaUSD);
        setBalanceHistory(data.recentTransactions);
        console.log('Balance updated:', data.balance.arena, 'ARENA');
      } else {
        console.error('Failed to fetch balance:', data.error);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const getValidToken = () => {
    const token = localStorage.getItem('token');
    
    // Check if token exists and is valid
    if (!token || token === 'null' || token === 'undefined' || token === '') {
      localStorage.removeItem('token'); // Clean up invalid token
      return null;
    }
    
    // Check if it looks like a JWT (has 3 parts)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid token format detected, removing...');
      localStorage.removeItem('token');
      return null;
    }
    
    return token;
  };

  // Fetch balance when wallet connects or price updates
  useEffect(() => {
    if (connected && publicKey && arenaPrice > 0) {
      fetchUserBalance();
    }
  }, [connected, publicKey, arenaPrice]);

  // Update wallet balances
  const updateWalletBalances = async () => {
    if (!publicKey) return;
    
    try {
      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const pubkey = publicKey;
      
      // Get SOL balance
      const balance = await connection.getBalance(pubkey);
      setSolBalance(balance / LAMPORTS_PER_SOL);
      
      // Get ARENA token balance
      try {
        const tokenAccountAddress = await getAssociatedTokenAddress(
          ARENA_TOKEN_MINT,
          pubkey
        );
        
        const tokenAccountInfo = await connection.getAccountInfo(tokenAccountAddress);
        
        if (tokenAccountInfo) {
          const tokenBalance = await connection.getTokenAccountBalance(tokenAccountAddress);
          if (tokenBalance && tokenBalance.value) {
            setArenaBalance(parseFloat(tokenBalance.value.uiAmount || 0));
          } else {
            setArenaBalance(0);
          }
        } else {
          setArenaBalance(0);
        }
      } catch (error) {
        console.error('Error fetching ARENA balance:', error);
        setArenaBalance(0);
      }
    } catch (error) {
      console.error('Error fetching balances:', error);
      setSolBalance(0);
      setArenaBalance(0);
    }
  };

  // Update wallet balances when wallet connects
  useEffect(() => {
    if (connected && publicKey) {
      setTimeout(() => {
        updateWalletBalances();
      }, 500);
    }
  }, [connected, publicKey]);

  // Play sound function
  const playSound = (soundKey) => {
    if (!soundEnabled) return;
    const audioElement = audioRefs.current[soundKey];
    if (!audioElement) return;
    const sound = audioElement.cloneNode();
    sound.play().catch(e => console.log("Audio play prevented:", e));
  };

  // Calculate ball path through pegs with smooth curves
  const calculateBallPath = () => {
    const keyPoints = [];
    let position = 0; // Start at center (0 position)
    
    // Add starting position above the first peg
    keyPoints.push({ x: 0, y: START_Y - 20, position: 0 });
    
    // Generate key points at each peg row
    for (let row = 0; row < ROWS; row++) {
      // At each row, the ball can go left (-0.5) or right (+0.5)
      const direction = Math.random() < 0.5 ? -0.5 : 0.5;
      position += direction;
      
      // Clamp position to valid range for this row
      const maxPosition = (row + 1) / 2;
      position = Math.max(-maxPosition, Math.min(maxPosition, position));
      
      keyPoints.push({ 
        x: position * PEG_SPACING, 
        y: START_Y + row * ROW_HEIGHT,
        position: position 
      });
    }
    
    // Generate smooth path with many interpolated points
    const smoothPath = [];
    for (let i = 0; i < keyPoints.length - 1; i++) {
      const start = keyPoints[i];
      const end = keyPoints[i + 1];
      
      // Create a curved path between pegs
      const steps = 30; // Increased steps for even smoother animation at slower speed
      for (let step = 0; step <= steps; step++) {
        const t = step / steps;
        
        // Use a parabolic curve to simulate bounce physics
        const bounceHeight = 4 * t * (1 - t) * 6; // Reduced bounce height
        
        // Add some horizontal wobble for realism
        const wobble = Math.sin(t * Math.PI) * 1.5; // Reduced wobble
        
        smoothPath.push({
          x: start.x + (end.x - start.x) * t + wobble,
          y: start.y + (end.y - start.y) * t - bounceHeight,
          peg: i // Track which peg we're near
        });
      }
    }
    
    // Add final descent to bucket with slower fall
    const lastPoint = keyPoints[keyPoints.length - 1];
    for (let i = 1; i <= 15; i++) { // More points for smoother descent
      smoothPath.push({
        x: lastPoint.x,
        y: lastPoint.y + (i * 2), // Slower descent
        peg: -1
      });
    }
    
    // Calculate final bucket
    const maxPosition = ROWS / 2;
    const normalizedPosition = (position + maxPosition) / (2 * maxPosition);
    
    let bucketIndex;
    const pos = normalizedPosition;
    
    if (pos <= 0.06) bucketIndex = 0;
    else if (pos <= 0.17) bucketIndex = 1;
    else if (pos <= 0.29) bucketIndex = 2;
    else if (pos <= 0.42) bucketIndex = 3;
    else if (pos <= 0.58) bucketIndex = 4;
    else if (pos <= 0.71) bucketIndex = 5;
    else if (pos <= 0.83) bucketIndex = 6;
    else if (pos <= 0.94) bucketIndex = 7;
    else bucketIndex = 8;
    
    return { path: smoothPath, bucketIndex, keyPoints };
  };

  // Smooth physics-based ball animation
  const animateBallDrop = async (path, bucketIndex) => {
    return new Promise((resolve) => {
      let pathIndex = 0;
      let lastTime = performance.now();
      let velocity = 0;
      const gravity = 0.15; // Reduced gravity for slower fall
      const maxVelocity = 2.5; // Reduced max speed
      let lastPeg = -1;
      
      const animate = (currentTime) => {
        const deltaTime = (currentTime - lastTime) / 16; // Normalize to ~60fps
        lastTime = currentTime;
        
        // Apply physics with reduced acceleration
        velocity = Math.min(velocity + gravity * deltaTime, maxVelocity);
        
        // Move along path based on velocity (with speed reduction)
        pathIndex += velocity * deltaTime * 0.6; // Added 0.6 multiplier to slow down overall
        
        if (pathIndex < path.length - 1) {
          // Get current position by interpolating between path points
          const index = Math.floor(pathIndex);
          const fraction = pathIndex - index;
          
          if (index < path.length - 1) {
            const current = path[index];
            const next = path[index + 1];
            
            // Smooth interpolation with slight easing
            const easedFraction = fraction < 0.5 
              ? 2 * fraction * fraction 
              : 1 - Math.pow(-2 * fraction + 2, 2) / 2;
            
            const interpolatedPoint = {
              x: current.x + (next.x - current.x) * easedFraction,
              y: current.y + (next.y - current.y) * easedFraction
            };
            
            setCurrentBall(interpolatedPoint);
            
            // Play sound when passing a new peg
            if (current.peg !== lastPeg && current.peg >= 0) {
              lastPeg = current.peg;
              if (soundEnabled && audioRefs.current['PEG_HIT']) {
                const sound = audioRefs.current['PEG_HIT'].cloneNode();
                sound.volume = 0.15 + Math.random() * 0.1; // Quieter sounds
                sound.playbackRate = 0.9 + Math.random() * 0.2;
                sound.play().catch(e => {});
              }
              // Reduce velocity on peg hit for bounce effect
              velocity *= 0.65; // More velocity reduction on bounce
            }
          }
        } else {
          // Ball reached bottom
          const multiplier = MULTIPLIERS[risk][bucketIndex];
          const winAmountUSD = betAmountUSD * multiplier;
          
          // Highlight the winning bucket
          setHighlightedBucket(bucketIndex);
          
          // Play win/lose sounds
          if (multiplier >= 10) {
            playSound('WIN_BIG');
          } else if (multiplier >= 2) {
            playSound('WIN_MEDIUM');
          } else if (multiplier >= 1) {
            playSound('WIN_SMALL');
          } else {
            playSound('LOSE');
          }
          
          setCurrentBall(null);
          
          // Clear highlight after 3 seconds
          setTimeout(() => {
            setHighlightedBucket(null);
          }, 3000);
          
          resolve({ multiplier, winAmountUSD, bucketIndex });
          return;
        }
        
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
    });
  };

  // Process a plinko drop using balance
  const processDrop = async () => {
    if (!connected || !publicKey) {
      handleError("Please connect your wallet first!");
      playSound('LOSE');
      return;
    }

    const betAmountArena = calculateArenaAmount(betAmountUSD, arenaPrice);

    if (userBalance < betAmountArena) {
      handleError(`Insufficient balance. You need ${betAmountArena.toFixed(2)} ARENA ($${betAmountUSD})`);
      setShowDepositModal(true);
      playSound('LOSE');
      return;
    }

    try {
      setIsDropping(true);
      setHighlightedBucket(null); // Clear any previous highlight
      playSound('BALL_DROP');

      // Calculate and animate ball path first
      const { path, bucketIndex, keyPoints } = calculateBallPath();
      setBallPath(keyPoints); // Store key points for visualization if needed
      
      const result = await animateBallDrop(path, bucketIndex);
      
      // Process backend with balance
      const token = getValidToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/plinko/drop-balance', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          betAmountUSD,
          betAmountArena,
          risk,
          bucketIndex,
          multiplier: result.multiplier,
          winAmountUSD: result.winAmountUSD,
          walletAddress: publicKey.toString(),
          arenaPrice
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update local balance
        setUserBalance(data.balanceAfter);
        setUserBalanceUSD(data.balanceAfter * arenaPrice);
        
        // Update history
        setDropHistory(prev => [{
          id: data.gameId,
          betAmountUSD,
          multiplier: result.multiplier,
          winAmountUSD: result.winAmountUSD,
          risk,
          timestamp: new Date()
        }, ...prev].slice(0, 10));
        
        // Update stats
        setTotalWagered(prev => prev + betAmountUSD);
        setTotalWon(prev => prev + result.winAmountUSD);
        
        // Show win message
        if (result.multiplier >= 1) {
          handleSuccess(`Won $${result.winAmountUSD.toFixed(2)}! (${result.multiplier}x)`);
        } else {
          handleInfo(`${result.multiplier}x - Better luck next time!`);
        }
      } else {
        handleError(data.error || "Failed to process drop");
      }
      
    } catch (error) {
      console.error('Drop error:', error);
      handleError(error.message || "Failed to process drop");
      playSound('LOSE');
    } finally {
      setIsDropping(false);
      setBallPath([]);
    }
  };

  // Deposit ARENA to balance
  const processDeposit = async () => {
    if (!connected || !publicKey) {
      handleError("Please connect your wallet first!");
      return;
    }

    if (arenaBalance < depositAmount) {
      handleError(`Insufficient wallet balance. You have ${arenaBalance.toFixed(2)} ARENA`);
      return;
    }

    try {
      setIsDepositing(true);
      setTransactionStatus("Preparing transaction...");

      const connection = new Connection(ALCHEMY_RPC_URL, 'confirmed');
      const fromPubkey = publicKey;
      
      const fromTokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        fromPubkey
      );
      
      const toTokenAccount = await getAssociatedTokenAddress(
        ARENA_TOKEN_MINT,
        PROJECT_WALLET
      );
      
      const fromAccountInfo = await connection.getAccountInfo(fromTokenAccount);
      const toAccountInfo = await connection.getAccountInfo(toTokenAccount);
      
      if (!fromAccountInfo) {
        throw new Error("Your ARENA token account doesn't exist");
      }
      
      const transaction = new Transaction();
      
      if (!toAccountInfo) {
        const createATAInstruction = createAssociatedTokenAccountInstruction(
          fromPubkey,
          toTokenAccount,
          PROJECT_WALLET,
          ARENA_TOKEN_MINT
        );
        transaction.add(createATAInstruction);
      }
      
      const transferInstruction = createTransferInstruction(
        fromTokenAccount,
        toTokenAccount,
        fromPubkey,
        depositAmount * Math.pow(10, ARENA_DECIMALS)
      );
      
      transaction.add(transferInstruction);
      
      setTransactionStatus("Waiting for wallet approval...");
      
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;
      
      const signature = await sendTransaction(transaction, connection);
      console.log("Transaction sent:", signature);
      
      setTransactionStatus("Transaction sent! Waiting for confirmation...");
      
      // Wait a bit longer for transaction to propagate
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Process deposit in backend
      const token = getValidToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/balance/deposit', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: depositAmount,
          walletAddress: publicKey.toString(),
          transactionSignature: signature,
          arenaPrice
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setUserBalance(data.balanceAfter);
        setUserBalanceUSD(data.balanceAfter * arenaPrice);
        handleSuccess(`Successfully deposited ${depositAmount} ARENA!`);
        setShowDepositModal(false);
        setDepositAmount(100);
        
        // Update wallet balances
        await updateWalletBalances();
        
        // Force refresh balance to make sure it's synced
        setTimeout(() => {
          fetchUserBalance();
        }, 1000);
      } else {
        // Check if it's a retryable error
        if (data.retryable) {
          handleInfo(data.details || "Transaction is being confirmed. Please wait and refresh your balance.");
          
          // Try to refresh balance after a delay
          setTimeout(() => {
            fetchUserBalance();
            updateWalletBalances();
          }, 10000);
        } else if (data.existingTransaction) {
          // Transaction was already processed
          handleSuccess(`Deposit already processed!`);
          setShowDepositModal(false);
          setDepositAmount(100);
          await fetchUserBalance();
        } else {
          handleError(data.error || "Failed to process deposit");
        }
      }
      
    } catch (error) {
      console.error('Deposit error:', error);
      
      // Check if it's just a timeout error
      if (error.name === 'TransactionExpiredTimeoutError') {
        setTransactionStatus("Transaction submitted. Verifying...");
        handleInfo("Transaction submitted. Please wait a moment and refresh your balance.");
        
        // Try to refresh balance after a delay
        setTimeout(() => {
          fetchUserBalance();
          updateWalletBalances();
        }, 10000);
      } else {
        handleError(error.message || "Failed to process deposit");
      }
    } finally {
      setIsDepositing(false);
      setTransactionStatus("");
    }
  };

  // Withdraw ARENA from balance
  const processWithdraw = async () => {
    if (!connected || !publicKey) {
      handleError("Please connect your wallet first!");
      return;
    }

    if (userBalance < withdrawAmount) {
      handleError(`Insufficient balance. You have ${userBalance.toFixed(2)} ARENA`);
      return;
    }

    try {
      setIsWithdrawing(true);
      setWithdrawStatus("Processing withdrawal...");

      const response = await fetch('/api/balance/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount: withdrawAmount,
          walletAddress: publicKey.toString(),
          arenaPrice
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setUserBalance(data.balanceAfter);
        setUserBalanceUSD(data.balanceAfter * arenaPrice);
        
        handleSuccess(`Successfully withdrawn ${withdrawAmount} ARENA!`);
        setShowWithdrawModal(false);
        setWithdrawAmount(50);
        
        // Update wallet balances after a delay
        setTimeout(() => {
          updateWalletBalances();
        }, 5000);
        
        // Refresh balance
        setTimeout(() => {
          fetchUserBalance();
        }, 2000);
        
      } else {
        handleError(data.error || "Failed to process withdrawal");
      }
      
    } catch (error) {
      console.error('Withdrawal error:', error);
      handleError(error.message || "Failed to process withdrawal");
    } finally {
      setIsWithdrawing(false);
      setWithdrawStatus("");
    }
  };

  // Auto mode handlers
  const startAutoMode = () => {
    if (!autoMode) {
      setAutoMode(true);
      setCurrentAutoDrops(0);
      runAutoDrop();
    } else {
      stopAutoMode();
    }
  };

  const stopAutoMode = () => {
    setAutoMode(false);
    setCurrentAutoDrops(0);
    if (autoDropIntervalRef.current) {
      clearInterval(autoDropIntervalRef.current);
    }
  };

  const runAutoDrop = async () => {
    if (currentAutoDrops < autoDropCount && autoMode) {
      await processDrop();
      setCurrentAutoDrops(prev => prev + 1);
      
      if (currentAutoDrops + 1 < autoDropCount) {
        setTimeout(runAutoDrop, 3000); // Adjusted delay for slower animation
      } else {
        stopAutoMode();
      }
    }
  };

  useEffect(() => {
    if (autoMode && currentAutoDrops < autoDropCount) {
      const timer = setTimeout(runAutoDrop, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentAutoDrops, autoMode]);

  const handleError = (message) => {
    // You can implement a toast notification here
    console.error(message);
  };

  const handleSuccess = (message) => {
    // You can implement a toast notification here
    console.log(message);
    playSound('COINS');
  };

  const handleInfo = (message) => {
    // You can implement a toast notification here
    console.log(message);
  };

  // Calculate profit percentage
  const profitPercentage = totalWagered > 0 
    ? ((totalWon - totalWagered) / totalWagered * 100).toFixed(2)
    : 0;

  // Calculate bet amount in ARENA
  const betAmountArena = arenaPrice > 0 ? calculateArenaAmount(betAmountUSD, arenaPrice) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white font-mono relative overflow-hidden -mt-16">
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        
        @media (max-width: 640px) {
          .mobile-button {
            min-height: 40px;
            font-size: 13px;
          }
          .mobile-modal {
            max-height: 90vh;
            overflow-y: auto;
          }
        }
      `}</style>
      
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-pink-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full filter blur-3xl"></div>
      </div>

      {/* Main content */}
      <div className="relative w-full max-w-7xl mx-auto px-3 py-2 sm:px-4 sm:py-3 lg:py-4">
        {/* Header - Compact Mobile Version */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <button 
            onClick={() => {
              playSound('PEG_HIT');
              window.history.back();
            }}
            className="bg-gray-900/80 backdrop-blur-sm p-2 sm:p-2.5 rounded-lg sm:rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
          >
            <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <div className="text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 tracking-wider">
              PLINKO
            </h1>
          </div>
          
          <div className="flex items-center space-x-1">
            {/* <button 
              onClick={() => {
                playSound('PEG_HIT');
                setSoundEnabled(!soundEnabled);
              }}
              className="bg-gray-900/80 backdrop-blur-sm p-2 sm:p-2.5 rounded-lg sm:rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
            >
              {soundEnabled ? (
                <SpeakerWaveIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <SpeakerXMarkIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button> */}
            
            <div className="relative">
              <button
                onClick={() => {
                  if (connected) {
                    setShowWalletDropdown(!showWalletDropdown);
                  } else {
                    setVisible(true);
                  }
                }}
                className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl backdrop-blur-sm border transition-all ${
                  connected 
                    ? 'bg-green-900/30 border-green-700/50 hover:border-green-600 shadow-lg shadow-green-900/20' 
                    : 'bg-red-900/30 border-red-700/50 hover:border-red-600 shadow-lg shadow-red-900/20'
                }`}
              >
                <WalletIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${connected ? 'text-green-400' : 'text-red-400'}`} />
              </button>

              {showWalletDropdown && connected && publicKey && (
                <div className="absolute right-0 mt-2 bg-gray-900/95 backdrop-blur-md rounded-xl border border-gray-700 shadow-2xl overflow-hidden z-50 w-56 sm:w-64">
                  <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-800">
                    <div className="text-xs text-gray-400">Connected</div>
                    <div className="text-xs sm:text-sm font-medium text-white break-all">
                      {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}
                    </div>
                    <div className="flex gap-3 mt-1 text-xs">
                      <span className="text-yellow-400">{arenaBalance.toFixed(2)} ARENA</span>
                      <span className="text-blue-400">{solBalance.toFixed(3)} SOL</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      disconnect();
                      setShowWalletDropdown(false);
                    }}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 text-left text-sm hover:bg-gray-800/50 transition-colors flex items-center space-x-2 text-red-400"
                  >
                    <span>Disconnect</span>
                  </button>
                </div>
              )}
            </div>
            
            {/* <button 
              onClick={() => {
                playSound('PEG_HIT');
                setShowStats(!showStats);
              }}
              className="bg-gray-900/80 backdrop-blur-sm p-2 sm:p-2.5 rounded-lg sm:rounded-xl border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-all"
            >
              <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            </button> */}
          </div>
        </div>

        {/* Balance Display - Compact Mobile Version */}
        <div className="bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md border border-gray-800/50 rounded-2xl p-3 sm:p-4 lg:p-5 mb-3 sm:mb-4 shadow-2xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-400 uppercase tracking-wider">Balance</span>
              </div>
              <div className="flex items-baseline space-x-2">
                <div className="text-xl sm:text-2xl lg:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
                  {isLoadingBalance ? (
                    <span className="animate-pulse">---</span>
                  ) : (
                    userBalance.toFixed(2)
                  )}
                </div>
                <span className="text-sm sm:text-base lg:text-lg font-bold text-yellow-400/70">ARENA</span>
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                ≈ ${userBalanceUSD.toFixed(2)}
              </div>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => {
                  playSound('PEG_HIT');
                  fetchUserBalance();
                }}
                disabled={isLoadingBalance}
                className="p-2 sm:p-2.5 bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white rounded-lg sm:rounded-xl transition-all disabled:opacity-50"
                title="Refresh"
              >
                <svg className={`h-4 w-4 sm:h-5 sm:w-5 ${isLoadingBalance ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              
              <button
                onClick={() => setShowWithdrawModal(true)}
                disabled={!connected || userBalance <= 0}
                className="p-2 sm:px-3 sm:py-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold rounded-lg sm:rounded-xl transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                <ArrowUpIcon className="h-4 w-4" />
                <span className="hidden sm:inline ml-1 text-sm">Withdraw</span>
              </button>
              
              <button
                onClick={() => setShowDepositModal(true)}
                disabled={!connected}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold rounded-lg sm:rounded-xl transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                <PlusIcon className="h-4 w-4 sm:mr-1" />
                <span className="text-sm">Deposit</span>
              </button>
            </div>
          </div>
          
          {/* Recent Activity - Collapsed on Mobile */}
          {balanceHistory.length > 0 && (
            <div className="hidden sm:block mt-3 pt-3 border-t border-gray-800">
              <div className="text-xs text-gray-500 mb-1">Recent Activity</div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">
                  {balanceHistory[0].type === 'deposit' ? '⬇️ Deposit' : 
                   balanceHistory[0].type === 'withdraw' ? '⬆️ Withdraw' :
                   balanceHistory[0].type === 'game_bet' ? '🎮 Bet' :
                   balanceHistory[0].type === 'game_win' ? '🏆 Win' : balanceHistory[0].type}
                </span>
                <span className={`font-semibold ${
                  balanceHistory[0].amount > 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {balanceHistory[0].amount > 0 ? '+' : ''}{balanceHistory[0].amount.toFixed(2)} ARENA
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Layout: Game board and controls stacked */}
        {/* Desktop Layout: Game board left, controls right */}
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
          {/* Game Board - Always visible at top on mobile */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 backdrop-blur-md border border-gray-700/50 rounded-xl sm:rounded-2xl p-2 sm:p-3 lg:p-4 shadow-xl">
              <div className="relative">
                {/* FIXED PLINKO BOARD */}
                <div ref={boardRef} className="relative mx-auto" style={{ maxWidth: '600px' }}>
                  <svg viewBox={`0 0 ${BOARD_WIDTH} ${BOARD_HEIGHT}`} className="w-full" preserveAspectRatio="xMidYMid meet">
                    <defs>
                      <filter id="pegGlow">
                        <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                      <filter id="bucketHighlight" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                      <linearGradient id="pegGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#F3F4F6" />
                        <stop offset="100%" stopColor="#D1D5DB" />
                      </linearGradient>
                      <radialGradient id="ballGradient" cx="30%" cy="30%">
                        <stop offset="0%" stopColor="#FEF3C7" />
                        <stop offset="50%" stopColor="#FDE047" />
                        <stop offset="100%" stopColor="#FACC15" />
                      </radialGradient>
                      <linearGradient id="boardGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#1E293B" />
                        <stop offset="100%" stopColor="#0F172A" />
                      </linearGradient>
                      <pattern id="gridPattern" patternUnits="userSpaceOnUse" width="40" height="40">
                        <line x1="0" y1="0" x2="0" y2="40" stroke="#334155" strokeWidth="0.5" opacity="0.3"/>
                        <line x1="0" y1="0" x2="40" y2="0" stroke="#334155" strokeWidth="0.5" opacity="0.3"/>
                      </pattern>
                      <linearGradient id="dropZoneGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#1E293B" stopOpacity="0"/>
                        <stop offset="100%" stopColor="#1E293B" stopOpacity="0.3"/>
                      </linearGradient>
                      
                      {/* Animation for pegs */}
                      <style>
                        {`
                          @keyframes pegShake {
                            0%, 100% { transform: translateX(0); }
                            25% { transform: translateX(-1px); }
                            75% { transform: translateX(1px); }
                          }
                          .peg-shake {
                            animation: pegShake 0.2s ease-in-out;
                          }
                          
                          @keyframes bucketPulse {
                            0%, 100% { 
                              opacity: 1;
                              transform: scale(1);
                            }
                            50% { 
                              opacity: 0.8;
                              transform: scale(1.05);
                            }
                          }
                          
                          @keyframes bucketGlow {
                            0% { 
                              opacity: 0.3;
                            }
                            50% { 
                              opacity: 0.8;
                            }
                            100% {
                              opacity: 0.3;
                            }
                          }
                        `}
                      </style>
                    </defs>
                    
                    {/* Background */}
                    <rect x="0" y="0" width={BOARD_WIDTH} height={BOARD_HEIGHT} fill="url(#boardGradient)" opacity="0.9" rx="8"/>
                    <rect x="0" y="0" width={BOARD_WIDTH} height={BOARD_HEIGHT} fill="url(#gridPattern)" opacity="0.5" rx="8"/>
                    
                    {/* Drop point indicator */}
                    <circle
                      cx={BOARD_WIDTH / 2}
                      cy={START_Y - 20}
                      r="6"
                      fill="none"
                      stroke="#FBBF24"
                      strokeWidth="1"
                      opacity="0.3"
                      strokeDasharray="2,2"
                    />

                    {/* Draw pegs in proper triangular pattern */}
                    {Array.from({ length: ROWS }, (_, row) => {
                      const pegsInRow = row + 1; // Start with 1 peg, add 1 each row
                      const rowCenterX = BOARD_WIDTH / 2;
                      const rowY = START_Y + row * ROW_HEIGHT;
                      
                      return Array.from({ length: pegsInRow }, (_, pegIndex) => {
                        // Calculate x position for this peg
                        const xOffset = (pegIndex - (pegsInRow - 1) / 2) * PEG_SPACING;
                        const x = rowCenterX + xOffset;
                        
                        return (
                          <g key={`peg-${row}-${pegIndex}`}>
                            {/* Peg shadow */}
                            <ellipse
                              cx={x}
                              cy={rowY + 2}
                              rx={PEG_RADIUS + 1}
                              ry={PEG_RADIUS / 2}
                              fill="#000000"
                              opacity="0.4"
                            />
                            {/* Peg */}
                            <circle
                              cx={x}
                              cy={rowY}
                              r={PEG_RADIUS}
                              fill="url(#pegGradient)"
                              stroke="#94A3B8"
                              strokeWidth="0.8"
                              filter="url(#pegGlow)"
                            />
                            {/* Peg highlight */}
                            <circle
                              cx={x - 1}
                              cy={rowY - 1.5}
                              r={PEG_RADIUS / 2.5}
                              fill="#FFFFFF"
                              opacity="0.7"
                            />
                          </g>
                        );
                      });
                    })}

                    {/* Remove side walls for cleaner look */}

                    {/* Connector area between pegs and buckets */}
                    <rect
                      x={(BOARD_WIDTH / 2) - ((ROWS - 1) * PEG_SPACING / 2) - (PEG_SPACING * 0.6)}
                      y={START_Y + (ROWS - 1) * ROW_HEIGHT + 10}
                      width={(ROWS - 1) * PEG_SPACING + (PEG_SPACING * 1.2)}
                      height={BOARD_HEIGHT - 40 - (START_Y + (ROWS - 1) * ROW_HEIGHT + 10)}
                      fill="url(#dropZoneGradient)"
                    />

                    {/* Draw buckets aligned with the ball drop positions */}
                    {MULTIPLIERS[risk].map((multiplier, index) => {
                      // Buckets should extend slightly beyond the peg formation
                      // Bottom row has 12 pegs spanning (ROWS-1) * PEG_SPACING
                      const bottomRowSpan = (ROWS - 1) * PEG_SPACING;
                      // Add 50% of one peg spacing total (25% on each side) for edge balls
                      const totalBucketWidth = bottomRowSpan + (PEG_SPACING * 1.2);
                      const bucketWidth = totalBucketWidth / BUCKET_COUNT;
                      const startX = (BOARD_WIDTH / 2) - (totalBucketWidth / 2);
                      const x = startX + (index * bucketWidth) + (bucketWidth / 2);
                      
                      const isHighMultiplier = multiplier >= 10;
                      const isMediumMultiplier = multiplier >= 2;
                      const isHighlighted = highlightedBucket === index;
                      
                      return (
                        <g key={`bucket-${index}`}>
                          {/* Highlight glow effect when bucket is selected */}
                          {isHighlighted && (
                            <>
                              {/* Outer glow */}
                              <rect
                                x={x - bucketWidth / 2 - 3}
                                y={BOARD_HEIGHT - 43}
                                width={bucketWidth + 6}
                                height={41}
                                fill={isHighMultiplier ? '#EF4444' : isMediumMultiplier ? '#22C55E' : '#FDE047'}
                                opacity="0.6"
                                rx="5"
                                filter="url(#bucketHighlight)"
                                style={{ 
                                  animation: 'bucketGlow 0.5s ease-in-out infinite'
                                }}
                              />
                            </>
                          )}
                          
                          {/* Bucket background */}
                          <rect
                            x={x - bucketWidth / 2 + 1}
                            y={BOARD_HEIGHT - 40}
                            width={bucketWidth - 2}
                            height={35}
                            fill={
                              isHighlighted 
                                ? (isHighMultiplier ? '#EF4444' : isMediumMultiplier ? '#34D399' : '#FACC15')
                                : (isHighMultiplier ? '#DC2626' : isMediumMultiplier ? '#10B981' : '#475569')
                            }
                            opacity={isHighlighted ? "0.8" : (isHighMultiplier ? "0.4" : isMediumMultiplier ? "0.35" : "0.3")}
                            rx="3"
                            stroke={
                              isHighlighted 
                                ? '#FFFFFF'
                                : (isHighMultiplier ? '#EF4444' : isMediumMultiplier ? '#10B981' : '#64748B')
                            }
                            strokeWidth={isHighlighted ? "2.5" : "1.5"}
                            strokeOpacity={isHighlighted ? "0.9" : "0.8"}
                            style={isHighlighted ? {
                              animation: 'bucketPulse 0.5s ease-in-out infinite',
                              transformOrigin: 'center',
                              transformBox: 'fill-box'
                            } : {}}
                          />
                          
                          {/* Bucket separator */}
                          {index > 0 && !isHighlighted && (
                            <line
                              x1={x - bucketWidth / 2}
                              y1={BOARD_HEIGHT - 38}
                              x2={x - bucketWidth / 2}
                              y2={BOARD_HEIGHT - 8}
                              stroke="#334155"
                              strokeWidth="1"
                              opacity="0.4"
                            />
                          )}
                          
                          {/* Multiplier text */}
                          <text
                            x={x}
                            y={BOARD_HEIGHT - 18}
                            textAnchor="middle"
                            fill={
                              isHighlighted 
                                ? '#FFFFFF'
                                : (isHighMultiplier ? '#FCA5A5' : isMediumMultiplier ? '#86EFAC' : '#E2E8F0')
                            }
                            fontSize={isHighlighted ? "14" : "12"}
                            fontWeight={isHighlighted ? "900" : "bold"}
                            style={{ 
                              textShadow: isHighlighted 
                                ? '0 0 20px rgba(0,0,0,0.9), 0 0 10px rgba(255,255,255,0.5)' 
                                : '0 0 10px rgba(0,0,0,0.8)'
                            }}
                          >
                            {multiplier}×
                          </text>
                        </g>
                      );
                    })}

                    {/* Draw current ball with better styling and motion blur */}
                    {currentBall && (
                      <g>
                        {/* Motion trail */}
                        <defs>
                          <filter id="motionBlur">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="0,1" />
                          </filter>
                        </defs>
                        
                        {/* Ball trail for motion blur effect */}
                        <circle
                          cx={BOARD_WIDTH / 2 + currentBall.x}
                          cy={currentBall.y - 2}
                          r="7"
                          fill="#FBBF24"
                          opacity="0.2"
                          filter="url(#motionBlur)"
                        />
                        
                        {/* Ball shadow */}
                        <ellipse
                          cx={BOARD_WIDTH / 2 + currentBall.x}
                          cy={currentBall.y + 4}
                          rx="10"
                          ry="5"
                          fill="#000000"
                          opacity="0.3"
                        />
                        
                        {/* Ball glow effect */}
                        <circle
                          cx={BOARD_WIDTH / 2 + currentBall.x}
                          cy={currentBall.y}
                          r="12"
                          fill="#FBBF24"
                          opacity="0.3"
                          filter="url(#pegGlow)"
                        >
                          <animate
                            attributeName="r"
                            values="12;15;12"
                            dur="0.8s"
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.3;0.1;0.3"
                            dur="0.8s"
                            repeatCount="indefinite"
                          />
                        </circle>
                        
                        {/* Main ball */}
                        <circle
                          cx={BOARD_WIDTH / 2 + currentBall.x}
                          cy={currentBall.y}
                          r="8"
                          fill="url(#ballGradient)"
                          stroke="#F59E0B"
                          strokeWidth="0.5"
                        />
                        
                        {/* Ball highlight */}
                        <ellipse
                          cx={BOARD_WIDTH / 2 + currentBall.x - 2.5}
                          cy={currentBall.y - 2.5}
                          rx="3"
                          ry="2.5"
                          fill="#FEF3C7"
                          opacity="0.8"
                        />
                        
                        {/* Inner shine */}
                        <circle
                          cx={BOARD_WIDTH / 2 + currentBall.x - 1}
                          cy={currentBall.y - 1}
                          r="1"
                          fill="#FFFFFF"
                          opacity="0.9"
                        />
                      </g>
                    )}
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Controls Panel - Below game board on mobile, side by side on desktop */}
          <div className="space-y-2 sm:space-y-3 lg:space-y-4">
            {/* Quick Stats - Mobile Only */}
            <div className="lg:hidden bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md border border-gray-800/50 rounded-xl p-2.5 shadow-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3 text-xs">
                  <span className="text-gray-500">Bet:</span>
                  <span className="font-bold text-yellow-400">${betAmountUSD}</span>
                </div>
                <div className="flex items-center space-x-3 text-xs">
                  <span className="text-gray-500">Risk:</span>
                  <span className="font-bold capitalize">
                    <span className={
                      risk === 'high' ? 'text-red-400' :
                      risk === 'medium' ? 'text-yellow-400' :
                      'text-green-400'
                    }>{risk}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Betting Controls - Compact Mobile */}
            <div className="bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md border border-gray-800/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-2xl">
              {/* <h3 className="text-sm sm:text-base lg:text-lg font-semibold mb-2 sm:mb-3 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
                Game Controls
              </h3> */}

              {/* Risk Level - Compact */}
              <div className="mb-3 sm:mb-4">
                <label className="text-xs sm:text-sm text-gray-400 mb-2 block uppercase tracking-wider">Risk Level</label>
                <div className="grid grid-cols-3 gap-2">
                  {['low', 'medium', 'high'].map(level => (
                    <button
                      key={level}
                      onClick={() => setRisk(level)}
                      disabled={isDropping}
                      className={`mobile-button relative px-2 py-2 rounded-lg font-semibold capitalize transition-all text-xs sm:text-sm ${
                        risk === level
                          ? level === 'high' 
                            ? 'bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-lg shadow-red-600/30' 
                            : level === 'medium'
                            ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow-lg shadow-yellow-600/30'
                            : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/30'
                          : 'bg-gray-800/80 hover:bg-gray-700/80 text-gray-400 border border-gray-700'
                      } ${isDropping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {level}
                      {risk === level && (
                        <div className="absolute inset-0 rounded-lg bg-white/10 animate-pulse"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bet Amount - Compact Mobile */}
              <div className="mb-3 sm:mb-4">
                <label className="text-xs sm:text-sm text-gray-400 mb-2 block uppercase tracking-wider">Bet Amount</label>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[1, 2, 3].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setBetAmountUSD(amount)}
                      disabled={isDropping}
                      className={`mobile-button px-2 py-2 rounded-lg font-bold transition-all text-xs sm:text-sm ${
                        betAmountUSD === amount
                          ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black shadow-lg shadow-yellow-500/30'
                          : 'bg-gray-800/80 hover:bg-gray-700/80 text-gray-400 border border-gray-700'
                      } ${isDropping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {[4, 5, 10].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setBetAmountUSD(amount)}
                      disabled={isDropping}
                      className={`mobile-button px-2 py-2 rounded-lg font-bold transition-all text-xs sm:text-sm ${
                        betAmountUSD === amount
                          ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black shadow-lg shadow-yellow-500/30'
                          : 'bg-gray-800/80 hover:bg-gray-700/80 text-gray-400 border border-gray-700'
                      } ${isDropping ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <div className="bg-gray-900/50 rounded-lg p-2 border border-gray-800">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">ARENA</span>
                    <span className="text-sm font-bold text-yellow-400">
                      {betAmountArena.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Drop Button - More prominent on mobile */}
              <button
                onClick={processDrop}
                disabled={isDropping || !connected || userBalance < betAmountArena || isLoadingBalance}
                className={`mobile-button relative w-full py-3.5 sm:py-4 lg:py-5 rounded-xl font-black text-base sm:text-lg lg:text-xl tracking-wider transition-all duration-300 overflow-hidden ${
                  !isDropping && connected && userBalance >= betAmountArena && !isLoadingBalance
                    ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black shadow-2xl shadow-yellow-500/30 transform hover:scale-[1.02] hover:shadow-yellow-500/50 animate-pulse lg:animate-none'
                    : 'bg-gray-800/50 text-gray-600 cursor-not-allowed border border-gray-700'
                }`}
              >
                <span className="relative z-10">
                  {isDropping ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      DROPPING...
                    </span>
                  ) : (
                    `DROP BALL ($${betAmountUSD})`
                  )}
                </span>
                {!isDropping && connected && userBalance >= betAmountArena && !isLoadingBalance && (
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 to-amber-600 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                )}
              </button>

              {!connected && (
                <p className="text-center text-yellow-400 text-xs mt-3 animate-pulse">
                  Connect wallet to play
                </p>
              )}
            </div>

            {/* Auto Mode - Compact for Mobile */}
            {/* <div className="bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md border border-gray-800/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
                  Auto Play
                </h3>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={autoDropCount}
                    onChange={(e) => setAutoDropCount(Math.max(1, parseInt(e.target.value) || 1))}
                    disabled={autoMode}
                    className="w-16 sm:w-20 px-2 py-1 bg-gray-800/80 border border-gray-700 rounded-lg text-white text-sm focus:border-violet-500 focus:outline-none disabled:opacity-50"
                    min="1"
                    max="100"
                  />
                  <button
                    onClick={startAutoMode}
                    disabled={isDropping || !connected || userBalance < betAmountArena}
                    className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center text-sm ${
                      autoMode
                        ? 'bg-gradient-to-r from-red-600 to-pink-600 text-white'
                        : 'bg-gradient-to-r from-violet-600 to-purple-600 text-white'
                    } ${(isDropping || !connected || userBalance < betAmountArena) ? 'opacity-50' : ''}`}
                  >
                    {autoMode ? (
                      <>
                        <StopIcon className="h-4 w-4 mr-1" />
                        Stop
                      </>
                    ) : (
                      <>
                        <PlayIcon className="h-4 w-4 mr-1" />
                        Auto
                      </>
                    )}
                  </button>
                </div>
              </div>
              {autoMode && (
                <div className="text-xs text-center text-gray-400">
                  Progress: {currentAutoDrops}/{autoDropCount}
                </div>
              )}
            </div> */}

            {/* Recent Drops - Compact Mobile View */}
            <div className="bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md border border-gray-800/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-2xl">
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold mb-2 sm:mb-3 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Recent Drops
              </h3>

              <div className="space-y-1.5 sm:space-y-2 max-h-32 sm:max-h-48 lg:max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                {dropHistory.length === 0 ? (
                  <div className="text-center text-gray-500 text-sm py-4">
                    <div className="text-xs">No drops yet</div>
                  </div>
                ) : (
                  dropHistory.slice(0, 5).map((drop, index) => (
                    <div
                      key={drop.id || index}
                      className={`p-2 sm:p-3 rounded-lg border backdrop-blur-sm transition-all ${
                        drop.multiplier >= 10
                          ? 'bg-red-900/20 border-red-700/50'
                          : drop.multiplier >= 2
                          ? 'bg-green-900/20 border-green-700/50'
                          : 'bg-gray-800/50 border-gray-700/50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <span className={`text-lg sm:text-xl font-black ${
                            drop.multiplier >= 10 ? 'text-red-400' : 
                            drop.multiplier >= 2 ? 'text-green-400' : 
                            'text-gray-300'
                          }`}>
                            {drop.multiplier}x
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">
                            ${drop.winAmountUSD.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Modal - Mobile Optimized */}
        {showStats && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="mobile-modal bg-gradient-to-b from-gray-900 to-black rounded-2xl p-5 sm:p-6 max-w-md w-full border border-gray-800 shadow-2xl">
              <div className="flex justify-between items-center mb-5 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
                  Session Statistics
                </h2>
                <button 
                  onClick={() => {
                    playSound('PEG_HIT');
                    setShowStats(false);
                  }}
                  className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800/50 transition-all"
                >
                  <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-gray-900/50 p-3 sm:p-4 rounded-xl border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Wagered</div>
                  <div className="text-xl sm:text-2xl font-black text-yellow-400">
                    ${totalWagered.toFixed(2)}
                  </div>
                </div>

                <div className="bg-gray-900/50 p-3 sm:p-4 rounded-xl border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Won</div>
                  <div className="text-xl sm:text-2xl font-black text-green-400">
                    ${totalWon.toFixed(2)}
                  </div>
                </div>

                <div className="bg-gray-900/50 p-3 sm:p-4 rounded-xl border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Net Profit</div>
                  <div className={`text-xl sm:text-2xl font-black ${
                    totalWon >= totalWagered ? 'text-green-400' : 'text-red-400'
                  }`}>
                    ${(totalWon - totalWagered).toFixed(2)}
                  </div>
                  <div className={`text-xs mt-1 ${
                    totalWon >= totalWagered ? 'text-green-400/70' : 'text-red-400/70'
                  }`}>
                    {profitPercentage}%
                  </div>
                </div>

                <div className="bg-gray-900/50 p-3 sm:p-4 rounded-xl border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Drops</div>
                  <div className="text-xl sm:text-2xl font-black text-violet-400">
                    {dropHistory.length}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center text-xs text-gray-500">
                Statistics reset on page refresh
              </div>
            </div>
          </div>
        )}

        {/* Deposit Modal - Mobile Optimized */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="mobile-modal bg-gradient-to-b from-gray-900 to-black rounded-2xl p-5 sm:p-6 max-w-md w-full border border-gray-800 shadow-2xl">
              <div className="flex justify-between items-center mb-5 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">
                  Deposit ARENA
                </h2>
                <button 
                  onClick={() => {
                    playSound('PEG_HIT');
                    setShowDepositModal(false);
                  }}
                  disabled={isDepositing}
                  className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800/50 transition-all disabled:opacity-50"
                >
                  <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs sm:text-sm text-gray-400 mb-2 sm:mb-3 block uppercase tracking-wider">
                    Deposit Amount
                  </label>
                  {/* <div className="grid grid-cols-3 gap-2 mb-3">
                    {[50, 100, 250].map(amount => (
                      <button
                        key={amount}
                        onClick={() => setDepositAmount(amount)}
                        disabled={isDepositing}
                        className={`mobile-button px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-bold transition-all transform hover:scale-105 text-sm ${
                          depositAmount === amount
                            ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black shadow-lg shadow-yellow-500/30'
                            : 'bg-gray-800/80 hover:bg-gray-700/80 text-gray-400 border border-gray-700'
                        } ${isDepositing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {amount}
                      </button>
                    ))}
                  </div> */}
                  <div className="relative">
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(Math.max(1, parseInt(e.target.value) || 1))}
                      disabled={isDepositing}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800/80 border border-gray-700 rounded-xl text-white text-base sm:text-lg font-bold focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 disabled:opacity-50 transition-all"
                      min="1"
                    />
                    <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      ARENA
                    </div>
                  </div>
                  <div className="text-center text-sm text-gray-500 mt-2">
                    ≈ ${(depositAmount * arenaPrice).toFixed(2)} USD
                  </div>
                </div>

                <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-800">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Wallet Balance</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="text-xl sm:text-2xl font-black text-yellow-400">
                    {arenaBalance.toFixed(2)} ARENA
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    ≈ ${(arenaBalance * arenaPrice).toFixed(2)} USD
                  </div>
                </div>

                <button
                  onClick={processDeposit}
                  disabled={isDepositing || !connected || arenaBalance < depositAmount}
                  className={`mobile-button w-full py-3.5 sm:py-4 rounded-xl font-black text-base sm:text-lg tracking-wider transition-all duration-300 transform hover:scale-[1.02] ${
                    !isDepositing && connected && arenaBalance >= depositAmount
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-black shadow-2xl shadow-green-500/30 hover:shadow-green-500/50'
                      : 'bg-gray-800/50 text-gray-600 cursor-not-allowed border border-gray-700'
                  } flex items-center justify-center`}
                >
                  {isDepositing ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {transactionStatus || 'Processing...'}
                    </>
                  ) : (
                    <>
                      <BanknotesIcon className="h-5 sm:h-6 w-5 sm:w-6 mr-2" />
                      Deposit {depositAmount} ARENA
                    </>
                  )}
                </button>

                {transactionStatus && (
                  <div className="text-center">
                    <div className="inline-flex items-center space-x-1 text-xs text-gray-500">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></div>
                      <span>{transactionStatus}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Modal - Mobile Optimized */}
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
            <div className="mobile-modal bg-gradient-to-b from-gray-900 to-black rounded-2xl p-5 sm:p-6 max-w-md w-full border border-gray-800 shadow-2xl">
              <div className="flex justify-between items-center mb-5 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
                  Withdraw ARENA
                </h2>
                <button 
                  onClick={() => {
                    playSound('PEG_HIT');
                    setShowWithdrawModal(false);
                  }}
                  disabled={isWithdrawing}
                  className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800/50 transition-all disabled:opacity-50"
                >
                  <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs sm:text-sm text-gray-400 mb-2 sm:mb-3 block uppercase tracking-wider">
                    Withdrawal Amount
                  </label>
                  {/* <div className="grid grid-cols-3 gap-2 mb-3">
                    {[25, 50, 100].map(amount => (
                      <button
                        key={amount}
                        onClick={() => setWithdrawAmount(amount)}
                        disabled={isWithdrawing}
                        className={`mobile-button px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl font-bold transition-all transform hover:scale-105 text-sm ${
                          withdrawAmount === amount
                            ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black shadow-lg shadow-yellow-500/30'
                            : 'bg-gray-800/80 hover:bg-gray-700/80 text-gray-400 border border-gray-700'
                        } ${isWithdrawing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {amount}
                      </button>
                    ))}
                  </div> */}
                  <div className="relative">
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(Math.max(10, parseInt(e.target.value) || 10))}
                      disabled={isWithdrawing}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-800/80 border border-gray-700 rounded-xl text-white text-base sm:text-lg font-bold focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 disabled:opacity-50 transition-all"
                      min="10"
                      max={Math.floor(userBalance)}
                    />
                    <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      ARENA
                    </div>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm mt-2">
                    <span className="text-gray-500">≈ ${(withdrawAmount * arenaPrice).toFixed(2)} USD</span>
                    <span className="text-yellow-400">Min: 10 ARENA</span>
                  </div>
                </div>

                <div className="bg-gray-900/50 p-4 rounded-xl border border-gray-800">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Platform Balance</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="text-xl sm:text-2xl font-black text-yellow-400">
                    {userBalance.toFixed(2)} ARENA
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    ≈ ${userBalanceUSD.toFixed(2)} USD
                  </div>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-xl p-3">
                  <div className="flex items-start space-x-2">
                    <svg className="h-4 sm:h-5 w-4 sm:w-5 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-xs text-yellow-400/90">
                      <p className="font-semibold mb-1">Withdrawal Info</p>
                      <p>• ARENA tokens will be sent to your connected wallet</p>
                      <p>• Network fees are covered by the platform</p>
                      <p>• Allow 1-2 minutes for blockchain confirmation</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={processWithdraw}
                  disabled={isWithdrawing || !connected || userBalance < withdrawAmount || withdrawAmount < 10}
                  className={`mobile-button w-full py-3.5 sm:py-4 rounded-xl font-black text-base sm:text-lg tracking-wider transition-all duration-300 transform hover:scale-[1.02] ${
                    !isWithdrawing && connected && userBalance >= withdrawAmount && withdrawAmount >= 10
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50'
                      : 'bg-gray-800/50 text-gray-600 cursor-not-allowed border border-gray-700'
                  } flex items-center justify-center`}
                >
                  {isWithdrawing ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {withdrawStatus || 'Processing...'}
                    </>
                  ) : (
                    <>
                      <ArrowUpIcon className="h-5 sm:h-6 w-5 sm:w-6 mr-2" />
                      Withdraw {withdrawAmount} ARENA
                    </>
                  )}
                </button>

                {withdrawStatus && (
                  <div className="text-center">
                    <div className="inline-flex items-center space-x-1 text-xs text-gray-500">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse"></div>
                      <span>{withdrawStatus}</span>
                    </div>
                  </div>
                )}

                {userBalance < withdrawAmount && withdrawAmount >= 10 && (
                  <p className="text-center text-red-400 text-xs">
                    Insufficient balance. You have {userBalance.toFixed(2)} ARENA
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
